import { useEffect, useRef, useState } from "react";

export const prefersReducedMotion = () =>
  typeof window === "undefined" ||
  !window.matchMedia ||
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Marks [data-reveal] elements with data-in once they scroll into view.
// The attribute is set on the DOM directly so React re-renders never clear it.
export function useReveal() {
  useEffect(() => {
    const nodes = () => document.querySelectorAll("[data-reveal]:not([data-in])");

    if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
      nodes().forEach((el) => el.setAttribute("data-in", ""));
      return undefined;
    }

    document.documentElement.classList.add("motion");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute("data-in", "");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    nodes().forEach((el) => observer.observe(el));

    // Pick up elements that mount later, like expanded lists or late sections.
    const mutation = new MutationObserver(() => nodes().forEach((el) => observer.observe(el)));
    mutation.observe(document.getElementById("main"), { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutation.disconnect();
    };
  }, []);
}

// Returns the id of the section currently under the header.
export function useScrollSpy(ids) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => visible.set(e.target.id, e.isIntersecting));
        const current = ids.find((id) => visible.get(id));
        setActive(current || null);
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);

  return active;
}

// Writes page scroll progress (0 to 1) into a CSS variable on the given element.
export function useScrollProgress() {
  const ref = useRef(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      el.style.setProperty("--progress", progress.toFixed(4));
      el.toggleAttribute("data-scrolled", window.scrollY > 8);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  return ref;
}

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Counts a figure like "20%" or "96" up from `from` when it scrolls into view.
// Screen readers get the final value straight away.
export function CountUp({ value, from = 0, duration = 1000 }) {
  const match = /^(\d+)(.*)$/.exec(value);
  const target = match ? Number(match[1]) : 0;
  const suffix = match ? match[2] : "";
  const ref = useRef(null);
  const [shown, setShown] = useState(() =>
    !match || prefersReducedMotion() ? target : from
  );

  useEffect(() => {
    if (!match || prefersReducedMotion() || !("IntersectionObserver" in window)) {
      setShown(target);
      return undefined;
    }
    let frame = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / duration, 1);
          setShown(Math.round(from + (target - from) * easeOutCubic(t)));
          if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.6 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, from, duration]);

  if (!match) return value;

  return (
    <span ref={ref} className="count">
      <span aria-hidden="true">
        {shown}
        {suffix}
      </span>
      <span className="visually-hidden">{value}</span>
    </span>
  );
}
